import {AccessCodeAlphabet, LogLevel, Occupancy, Platform} from "@variocube/app-api";
import {EventSupportingApp, generateCode} from "@variocube/app-common";
import {
	AppContext,
	AppErrorBoundary,
	ThemeProvider,
	useActivateListener,
	usePlatform,
	useStorageItemValue,
} from "@variocube/cube-ui";
import {DateTime} from "luxon";
import * as React from "react";
import {useCallback, useState} from "react";
import {render} from "react-dom";
import {LocalizationProvider, SUPPORTED_LANGUAGES} from "../i18n";
import {Admin} from "./Admin";
import icon from "./delivery.svg";
import {HomeView} from "./HomeView";
import {LearnPickupKeyView} from "./learnPickupKey";
import {PickupView} from "./pickup";
import {ReturnDeliveryFlow} from "./ReturnDeliveryFlow";
import {StorageFlow} from "./StorageFlow";
import {
	Bookmark,
	BookmarkType,
	Delivery,
	DeliveryState,
	getOccupancyDeliveries,
	getReturnDeliveryConfig,
	LearnedPickupKey,
	OccupancyType,
	PickupEvent,
	Tenant,
	TenantUser,
	UseExistingBox,
} from "./types";

export default class extends EventSupportingApp {
	constructor(p: Platform) {
		super(p, icon, "#0d4b6c", SUPPORTED_LANGUAGES);
	}

	render(root: Element) {
		const appContext = {
			platform: this.platform,
			appEventSupport: this.eventSupport,
		};
		const defaultTheme = {
			colorBackground: "#f0f7fa",
			colorForeground: "#08212d",
			colorPrimary: "#0d4b6c",
			colorSeparator: "#56646b",
			colorInputBackground: "#fff",
		};
		render(
			<AppContext.Provider value={appContext}>
				<ThemeProvider defaultTheme={defaultTheme}>
					<AppErrorBoundary appId={this.platform.appId} cubeId={this.platform.hostname}>
						<LocalizationProvider app={this}>
							<App />
						</LocalizationProvider>
					</AppErrorBoundary>
				</ThemeProvider>
			</AppContext.Provider>,
			root,
		);
	}

	async loadMockData() {
		const background = this.platform.getArg("background");
		const bwi = this.platform.getArg("bwi"); // enables bwi specific texts

		let backgroundImage = undefined;
		if (background) {
			const backgroundResponse = await window.fetch(
				"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mPccePjfwAIUwOCOf5cIwAAAABJRU5ErkJggg==",
			);
			backgroundImage = await backgroundResponse.blob();
		}
		const uuid = generateCode(16);
		const storedDelivery: Delivery = {
			delivery: {
				deliveryId: uuid,
				isCreatedOnCube: true,
				state: DeliveryState.Stored,
			},
			bookmark: "it",
			recipients: [
				{recipientName: "Rick Sanchez", email: "rick@galactic.co", pickupKey: "454541"},
				{recipientName: "Matthias Steinbauer", email: "matthias.steinbauer@variocube.com", pickupKey: "454542"},
			],
			order: {
				orderId: generateCode(12),
				description: "Portal gun",
			},
			storage: {
				hazardousGoods: "Maximum danger",
				storageTimeMax: 9,
				destination: "Garage",
			},
			tags: ["Smarttag 1", "Smarttag 2", "stored"],
		};

		const pickedUpDelivery: Delivery = {
			delivery: {
				deliveryId: generateCode(16),
				state: DeliveryState.PickedUp,
				isCreatedOnCube: false,
			},
			bookmark: "it",
			recipients: [
				{recipientName: "Grigor Tonkov", email: "grigor.tonkov@gmail.com", pickupKey: "66666"},
			],
			order: {
				orderId: generateCode(12),
				description: "3 Eiskugeln",
			},
			storage: {
				hazardousGoods: "Maximum danger",
				storageTimeMax: 9,
				destination: "Kühlschrank",
			},
			tags: ["picked up"],
		};

		const occupancy = await this.platform.occupy({
			accessCode: "1",
			type: "M",
			content: storedDelivery,
		});
		await this.platform.confirmOccupancy(occupancy.uuid);
		console.info("created occupancy for delivery", occupancy);

		storedDelivery.delivery.boxNumber = occupancy.boxNumber;

		const exceededStorageTimeDelivery: Delivery = {
			delivery: {
				deliveryId: generateCode(16),
				isCreatedOnCube: false,
				state: DeliveryState.Stored,
				allowPickupUntil: DateTime.now().minus(1).toISO(),
			},
			bookmark: "it",
			recipients: [
				{recipientName: "Rick Sanchez", pickupKey: "454541"},
				{recipientName: "Matthias Steinbauer", pickupKey: "454542"},
			],
			order: {
				orderId: generateCode(12),
				description: "Portal gun",
			},
			storage: {
				hazardousGoods: "Maximum danger",
				storageTimeMax: 9,
				destination: "Garage",
			},
			tags: ["Smarttag 3", "Smarttag 4"],
		};

		const exceededStorageTimeOccupancy = await this.platform.occupy({
			accessCode: "444444",
			type: "M",
			content: exceededStorageTimeDelivery,
		});
		await this.platform.confirmOccupancy(exceededStorageTimeOccupancy.uuid);
		console.info("created occupancy for delivery exceeded storage time", exceededStorageTimeOccupancy);

		exceededStorageTimeDelivery.delivery.boxNumber = exceededStorageTimeOccupancy.boxNumber;

		const outboundMail: Delivery = {
			delivery: {
				deliveryId: generateCode(16),
				isCreatedOnCube: false,
				state: DeliveryState.Stored,
				allowPickupUntil: DateTime.now().minus(1).toISO(),
				deliveryType: OccupancyType.Outbound,
			},
			bookmark: "it",
			recipients: [
				{recipientName: "Mailroom"},
			],
			order: {
				orderId: generateCode(12),
				description: "100 cubes",
			},
			storage: {
				storageTimeMax: 1,
			},
		};

		const outboundMailOccupancy = await this.platform.occupy({
			accessCode: "444444",
			type: "M",
			content: outboundMail,
		});
		await this.platform.confirmOccupancy(outboundMailOccupancy.uuid);
		console.info("created occupancy for delivery exceeded storage time", outboundMailOccupancy);

		outboundMail.delivery.boxNumber = outboundMailOccupancy.boxNumber;

		const newDelivery: Delivery = {
			delivery: {
				deliveryId: generateCode(16),
				isCreatedOnCube: false,
				state: DeliveryState.Created,
			},
			recipients: [
				{recipientName: "Morty"},
			],
			order: {
				orderId: generateCode(12),
				description: "Toys",
			},
			storage: {
				hazardousGoods: "Harmless",
				storageTimeMax: 55,
				destination: "Morty's room",
				storageKey: "12345a",
				storageCode: "54321a",
			},
		};

		const newReturnDelivery: Delivery = {
			delivery: {
				deliveryId: generateCode(16),
				isCreatedOnCube: false,
				state: DeliveryState.Created,
			},
			recipients: [
				{recipientName: "Jerry"},
			],
			order: {
				orderId: generateCode(12),
				description: "Mr. Meeseeks",
			},
			storage: {
				hazardousGoods: "Handle with care",
				storageTimeMax: 55,
				destination: "Morty's room",
				storageKey: "46464",
				storageCode: "44444",
			},
			returnDelivery: {
				description: "Mr. Meeseeks",
				state: DeliveryState.Created,
			},
		};

		const addDelivery: Delivery = {
			delivery: {
				deliveryId: generateCode(16),
				isCreatedOnCube: false,
				state: DeliveryState.Created,
			},
			bookmark: "it",
			recipients: [
				{recipientName: "Rick Sanchez", email: "rick@galactic.co", pickupKey: "454541"},
				{recipientName: "Matthias Steinbauer", email: "matthias.steinbauer@variocube.com", pickupKey: "454542"},
			],
			order: {
				orderId: generateCode(12),
				description: "Another portal gun",
			},
			storage: {
				hazardousGoods: "Maximum danger",
				storageTimeMax: 9,
				destination: "Garage",
			},
		};

		const tenant: Tenant = {
			centerId: "variocube",
			name: "Variocube",
			nfcCardLearning: true,
			gracePeriod: 10,
			checkAfterPickup: true,
			createDeliveryFromCode: true,
			users: [
				{
					username: "Test",
					firstName: "Firstname",
					lastName: "Lastname",
					email: "office@example.com",
					accessCode: "999",
				},
			],
			useExistingBox: UseExistingBox.AskToAdd,
			returnDeliveryConfig: {
				enabled: true,
				returnRequiresAuthorization: false,
				parameters: {
					deliveryNoteId: {enabled: true, mandatory: true},
					email: {enabled: true, mandatory: true, verification: true},
					phone: {enabled: false, mandatory: false},
					additionalInformation: {enabled: true, mandatory: false},
				},
			},
			storageCountDown: null
		};

		const bookmarks: Bookmark[] = [
			{
				id: "it",
				type: BookmarkType.Department,
				label: "IT",
				recipients: [
					{
						recipientName: "Matthias Steinbauer",
						email: "matthias.steinbauer@variocube.com",
					},
				],
				cubeId: "localhost",
				enableAnonymousDelivery: true,
			},
			{
				id: "legal",
				type: BookmarkType.Department,
				label: "Legal",
				recipients: [
					{
						recipientName: "Matthias Steinbauer",
						email: "matthias.steinbauer@variocube.com",
					},
				],
			},
			{
				id: "hr",
				type: BookmarkType.Department,
				label: "HR",
				recipients: [
					{
						recipientName: "Matthias Steinbauer",
						email: "matthias.steinbauer@variocube.com",
					},
				],
			},
			{
				id: "mailroom",
				type: BookmarkType.Department,
				label: "Mailroom",
				recipients: [
					{
						recipientName: "Matthias Steinbauer",
						email: "matthias.steinbauer@variocube.com",
					},
				],
			},
			{
				id: "marketing",
				type: BookmarkType.Department,
				label: "Marketing",
				recipients: [
					{
						recipientName: "Matthias Steinbauer",
						email: "matthias.steinbauer@variocube.com",
					},
				],
			},
		];

		const theme = {
			colorPrimary: "#e10974",
			colorBackground: "#ffffff",
			colorForeground: "#1a010d",
			colorSeparator: "#663d52",
			colorInputBackground: "#fff",
		};

		return {
			tenant,
			bookmarks,
			backgroundImage,
			// tenantLogoUrl: tCubeLogo,  // uncomment to enable tenant logo
			// theme,                     // uncomment to enable tenant theme
			deliveries: [
				storedDelivery,
				exceededStorageTimeDelivery,
				newDelivery,
				newReturnDelivery,
				outboundMail,
				addDelivery,
			],
			pickedUpDeliveries: [pickedUpDelivery],
			backendUrl: "https://logistics.variocube.com/api/v2/",
			i18n: bwi
				? {
					de: {
						PickupConfirmMessage:
							"Wollen Sie Ihr Fach erneut öffnen, dann drücken Sie bitte auf ‚Fach erneut öffnen‘ -- ansonsten werden Sie in 20 Sekunden automatisch vom System abgemeldet (oder Sie drücken auf ‚Startbildschirm‘).",
						Confirm: "Startbildschirm",
						PickupConfirmTitle: "Übergabe erfolgt",
						AutoConfirmMessage: "Automatisch zum Startbildschirm in {{secondsLeft}} Sekunden...",
					},
				}
				: undefined,
		};
	}
}

function App() {
	const [adminUser, setAdminUser] = useState<TenantUser | undefined>();
	const [pickup, setPickup] = useState<PickupEvent>();
	const [storage, setStorage] = useState<Delivery>();
	const [returnCase, setReturnCase] = useState(false);
	const [learnPickupKey, setLearnPickupKey] = useState<boolean>(false);
	const [processingPartialPickup, setProcessingPartialPickup] = useState<boolean>(false);
	const platform = usePlatform();
	const tenant = useStorageItemValue<Tenant>("tenant");

	const [returnDelivery, setReturnDelivery] = useState<Delivery>();
	const [returnOccupancies, setReturnOccupancies] = useState<Occupancy[]>();

	const handleCreateDelivery = useCallback(async (delivery: Delivery) => {
		const tenant = await platform.getStorageItem<Tenant>("tenant");
		if (!tenant || !tenant.createDeliveryFromCode) {
			throw new Error("Tenant is not configured to create delivery from code");
		}
		setStorage(delivery);
	}, [platform]);

	const handleActivate = useCallback(() => {
		setAdminUser(undefined);
		setPickup(undefined);
	}, []);
	useActivateListener(handleActivate);

	const handleDelivery = useCallback((user: TenantUser) => setAdminUser(user), []);
	const handleDeliveryDone = useCallback(() => setAdminUser(undefined), []);
	const handleStorageDone = useCallback(() => setStorage(undefined), []);
	const handlePickupDone = useCallback(() => setPickup(undefined), []);

	const handleDeliveryPickupConfirm = useCallback(
		async (pickupEvent: PickupEvent, partialPickup: boolean, learnedPickupKey?: LearnedPickupKey) => {
			setPickup(undefined);
			setLearnPickupKey(false);
			for (let occupancy of pickupEvent.occupancies) {
				try {
					console.info("Updating occupancy", occupancy);
					const deliveries = getOccupancyDeliveries(occupancy) ?? [];

					for (const delivery of deliveries) {
						if (pickupEvent.pickupKey) {
							delivery.usedPickupKey = pickupEvent.pickupKey;
							await platform.log(LogLevel.Trace, "Stored usedPickupKey to occupancy", {
								usedPickupKey: pickupEvent.pickupKey,
							});
						}

						if (learnedPickupKey) {
							delivery.learnedPickupKey =
								`${learnedPickupKey.recipientIndex};${learnedPickupKey.pickupKey}`;
							await platform.log(LogLevel.Trace, "Stored learnedPickupKey to occupancy", {
								learnedPickupKey,
							});
						}
					}

					if (partialPickup) {
						// partial pickups are only logged by now
						await platform.log(LogLevel.Audit, "Partial recipient pickup", deliveries);
					} else {
						for (const delivery of deliveries) {
							delivery.delivery.state = DeliveryState.PickedUp;
							if (pickupEvent?.pickupRecipient) {
								delivery.delivery.pickedUpBy = pickupEvent.pickupRecipient.recipientName + " "
									+ pickupEvent.pickupRecipient.email;
							}
							if (delivery.returnDelivery) {
								if (delivery.returnDelivery.state == DeliveryState.Stored) {
									delivery.returnDelivery.state = DeliveryState.PickedUp;
								} else {
									delivery.returnDelivery.state = DeliveryState.Stored;
									await platform.changeAccessCode(occupancy.uuid, {
										accessCodeShape: {
											length: 5,
											alphabet: AccessCodeAlphabet.OneToNine,
										},
									});
								}
							}
						}
						if (
							deliveries.every(delivery =>
								!delivery.returnDelivery || delivery.returnDelivery.state == DeliveryState.PickedUp
							)
						) {
							await platform.endOccupancy(occupancy.uuid, {
								content: {deliveries},
								gracePeriod: tenant ? tenant.gracePeriod : undefined,
							});
						} else {
							await platform.updateOccupancy(occupancy.uuid, {deliveries});
						}
						await platform.log(LogLevel.Audit, "Recipient Pickup", {deliveries});
					}
				} catch (error) {
					console.error("Error updating occupancy", error);
				}
			}
		},
		[platform, tenant],
	);

	const handleDeliveryPickupConfirmPostActions = useCallback(
		async (pickupEvent: PickupEvent, partialPickup: boolean, learnNfcCard: boolean) => {
			setProcessingPartialPickup(partialPickup);
			if (learnNfcCard && !partialPickup) {
				setLearnPickupKey(true);
				return;
			}
			await handleDeliveryPickupConfirm(pickupEvent, partialPickup);
		},
		[platform, handleDeliveryPickupConfirm],
	);

	const handleReturn = useCallback(async (delivery: Delivery, pickup: PickupEvent) => {
		setReturnCase(true);
		setReturnDelivery(delivery);
		setReturnOccupancies(pickup.occupancies);
	}, [platform]);

	function handleReturnCancel() {
		setReturnCase(false);
		setReturnDelivery(undefined);
		setReturnOccupancies(undefined);
	}

	function handleReturnConfirm() {
		setPickup(undefined);
		setReturnCase(false);
		setReturnDelivery(undefined);
		setReturnOccupancies(undefined);
	}

	if (!tenant) {
		return null;
	}

	if (returnCase) {
		return (
			<ReturnDeliveryFlow
				delivery={returnDelivery}
				occupancies={returnOccupancies}
				config={getReturnDeliveryConfig(tenant)}
				onConfirm={handleReturnConfirm}
				onBack={handleReturnCancel}
			/>
		);
	}
	else if (pickup) {
		if (learnPickupKey) {
			return (
				<LearnPickupKeyView
					pickupEvent={pickup}
					partialPickup={processingPartialPickup}
					onConfirm={handleDeliveryPickupConfirm}
				/>
			);
		}
		// show order pickup flow
		return (
			<PickupView
				pickupEvent={pickup}
				onConfirm={handleDeliveryPickupConfirmPostActions}
				onCancel={handlePickupDone}
				onReturn={getReturnDeliveryConfig(tenant).enabled ? handleReturn : undefined}
			/>
		);
	}
	else if (storage) {
		// show delivery flow
		return (
			<StorageFlow
				delivery={storage}
				onDone={handleStorageDone}
			/>
		);
	}
	else if (adminUser) {
		// show admin menu
		return <Admin user={adminUser} onDone={handleDeliveryDone} />;
	}
	else {
		return (
			<HomeView
				returnDeliveriesEnabled={getReturnDeliveryConfig(tenant).enabled}
				onPickup={setPickup}
				onAdminLogin={handleDelivery}
				onStorage={setStorage}
				onReturn={() => setReturnCase(true)}
				onCreateDelivery={handleCreateDelivery}
			/>
		);
	}
}
